import type { StudentListRow, TeacherAssignment } from '../../api/types';

/** 1 -> "1st", 2 -> "2nd", 3 -> "3rd", 11 -> "11th". */
export function ordinal(n: number): string {
  const mod100 = n % 100;
  if (mod100 >= 11 && mod100 <= 13) return `${n}th`;
  const suffix = ({ 1: 'st', 2: 'nd', 3: 'rd' } as Record<number, string>)[n % 10] ?? 'th';
  return `${n}${suffix}`;
}

/** Students whose bucket starts at or above the pass mark, out of all students bucketed. */
export function passRate(histogram: Array<{ bucket: string; count: number }>, passMark: number) {
  let total = 0;
  let passing = 0;
  for (const b of histogram) {
    total += b.count;
    const lower = parseFloat(b.bucket);
    if (Number.isFinite(lower) && lower >= passMark) passing += b.count;
  }
  return { passing, total, pct: total > 0 ? (passing / total) * 100 : null };
}

/** Last minus first weekly average and the number of weeks that had a score. Null with fewer than two. */
export function trendChange(series: Array<{ avg_score: number | null }>): { change: number; weeks: number } | null {
  const scores = series.map((p) => p.avg_score).filter((v): v is number => v != null);
  if (scores.length < 2) return null;
  return { change: scores[scores.length - 1] - scores[0], weeks: scores.length };
}

/** Position by descending average (1 = best); students without an average get no rank. Ties share the lower position. */
export function rankByAverage(rows: Array<{ id: number; avg_score?: number | null }>): Map<number, number> {
  const scored = rows.filter((r) => r.avg_score != null).sort((a, b) => (b.avg_score as number) - (a.avg_score as number));
  const out = new Map<number, number>();
  scored.forEach((r, i) => {
    const prev = scored[i - 1];
    out.set(r.id, prev && prev.avg_score === r.avg_score ? (out.get(prev.id) as number) : i + 1);
  });
  return out;
}

/** Girls and boys from `gender`; null when no student carries the field (older backend). */
export function genderSplit(rows: Array<Pick<StudentListRow, 'gender'>>): { girls: number; boys: number } | null {
  if (!rows.some((r) => r.gender)) return null;
  const g = (r: Pick<StudentListRow, 'gender'>) => (r.gender ?? '').toLowerCase();
  return { girls: rows.filter((r) => g(r) === 'female').length, boys: rows.filter((r) => g(r) === 'male').length };
}

export function activityCounts(rows: Array<Pick<StudentListRow, 'engagement_dot' | 'open_alerts_count'>>) {
  return {
    active: rows.filter((r) => r.engagement_dot === 'active').length,
    idle: rows.filter((r) => r.engagement_dot === 'idle').length,
    silent: rows.filter((r) => r.engagement_dot === 'silent').length,
    openAlerts: rows.reduce((n, r) => n + (r.open_alerts_count ?? 0), 0),
  };
}

/** "2025-09-08T10:00:00Z" -> "Sep 2025". */
export function monthYear(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return `${['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][d.getMonth()]} ${d.getFullYear()}`;
}

/**
 * Subject teachers of a section: assignments to this grade that are grade-wide or for this section,
 * one per subject (a section-specific assignment wins over a grade-wide one), sorted by subject name.
 */
export function subjectTeachers(assignments: TeacherAssignment[], gradeId: number, sectionId: number) {
  const bySubject = new Map<number, TeacherAssignment>();
  for (const a of assignments) {
    if (a.school_grade !== gradeId || a.subject == null) continue;
    if (a.section != null && a.section.id !== sectionId) continue;
    const have = bySubject.get(a.subject);
    if (!have || (have.section == null && a.section != null)) bySubject.set(a.subject, a);
  }
  return [...bySubject.values()]
    .map((a) => ({ id: a.id, teacherId: a.teacher, name: a.teacher_name || a.teacher_username, subjectId: a.subject as number, subject: a.subject_name ?? '', isHomeroom: a.is_homeroom }))
    .sort((a, b) => a.subject.localeCompare(b.subject));
}

/**
 * What the assignments say about a section's homeroom teacher: the subject they teach it and since when.
 * `TeacherAssignment.teacher` is the teacher's UserProfile id, i.e. `Section.homeroom_teacher.id` (not `user_id`).
 */
export function homeroomInfo(assignments: TeacherAssignment[], teacherProfileId: number, gradeId: number, sectionId: number) {
  const mine = assignments.filter((a) => a.teacher === teacherProfileId && a.school_grade === gradeId
    && (a.section == null || a.section.id === sectionId));
  const subject = mine.find((a) => a.subject_name)?.subject_name ?? null;
  const flagged = mine.find((a) => a.is_homeroom && a.created_at);
  return { subject, since: monthYear(flagged?.created_at) };
}
